import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Backdrop, Button, Grid, TextField, Typography, useMediaQuery } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AppLoader from '../../../components/AppLoader/AppLoader';
import Error from '../../../components/error/error';
import ServiceDashboardTables from '../../../components/ServiceDashboardTables/ServiceDashboardTables';
import StepProgressBar from '../../../components/StepProgressBar/StepProgressBar';
import ProcessingSummaryTableBilling from '../../../components/processingSummaryTable/processingSummaryTableBilling';
import { tableStyle, tableStyleOverDue } from '../../../style/common';
import { DISCO_COMPANIES } from '../../../constants/discoCompanies';
import { getInitialAdminOfficeCode } from '../../../utils/adminCompany';
import {
  WORKFLOW_MOBILE_MEDIA,
  workflowDialogClassName,
  workflowDialogShellProps,
} from '../../../utils/workflowDialogShell';
import { convertDate, convertDatewithTime, numberFormatting, processInstallmentWithdrawal, subdivisionName } from '../../../services/Functions';
import { SearchIndividualApplication } from '../../../network/APIs/APIs';
import {
  fetchPaginatedWithdrawalOfInstallmentNormal,
  filterDateFrom as filterDateFromNormal,
  filterDateTo as filterDateToNormal,
  filterProgressId as filterProgressIdNormal,
  firstPage as firstPageNormal,
  lastPage as lastPageNormal,
  nextPage as nextPageNormal,
  previousPage as previousPageNormal,
  resetDateFilter as resetDateFilterNormal,
  resetProgressIdFilter as resetProgressIdFilterNormal,
} from '../../../features/billingAndCollection/withdrawalOfInstallmentNormalSlice';
import {
  fetchPaginatedWithdrawalOfInstallmentOverdue,
  filterDateFrom as filterDateFromOverdue,
  filterDateTo as filterDateToOverdue,
  filterProgressId as filterProgressIdOverdue,
  firstPage as firstPageOverdue,
  lastPage as lastPageOverdue,
  nextPage as nextPageOverdue,
  previousPage as previousPageOverdue,
  resetDateFilter as resetDateFilterOverdue,
  resetProgressIdFilter as resetProgressIdFilterOverdue,
} from '../../../features/billingAndCollection/withdrawalOfInstallmentOverdueSlice';
import '../../../style/common.scss';
import '../Installments/InstallmentsWorkflowDialog.scss';
import './WithdrawalOfInstallmentWorkflowDialog.scss';

const getClassName = (status) => {
  if (status === 'Pending') return 'pending-button';
  if (status === 'In Process') return 'inprocess-button';
  if (status === 'Approved') return 'approved-button';
  if (status === 'Objection') return 'objection-button';
  if (status === 'Rejected') return 'rejected-button';
  return '';
};

const statusFunc = (progressId, objectionStatus) => {
  if (Number(objectionStatus) === 1) return 'Objection';
  const progress = Number(progressId);
  if (progress === 0) return 'In Process';
  if (progress === 1) return 'Approved';
  if (progress === -1) return 'Rejected';
  return 'In Process';
};

const TELCO_LABELS = {
  1: 'Jazz / Warid',
  2: 'Ufone / Onic',
  3: 'Telenor',
  4: 'Zong',
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const displayAmount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return numberFormatting(value);
};

const displayDate = (value) => {
  if (!value) return '-';
  const formatted = convertDate(value);
  if (!formatted || formatted.includes('undefined') || formatted.includes('NaN')) {
    return String(value).replace('T', ' ').slice(0, 19);
  }
  return formatted;
};

const displayMonth = (value) => {
  if (!value) return '-';
  const text = String(value).trim();
  if (/^\d{2}-[A-Z]{3}-\d{2}$/i.test(text)) return text;
  return displayDate(value);
};

const telcoLabel = (code) => TELCO_LABELS[Number(code)] || displayValue(code);

const toProgressId = (val) => Number(val);

const WOI_STATUS_BAR = [
  { title1: 'APPLICATION', title2: 'SUBMITTED' },
  { title1: 'ACTION', title2: 'TAKEN' },
  { title1: 'COMPLETED', title2: '' },
];

// Pending (0): Action Taken is current (orange number).
// Rejected (-1): rejection stays on Action Taken.
// Approved (1): all steps done, Completed has a green tick.
const getWithdrawalStepIndex = (row) => {
  const progressId = toProgressId(row?.applicationProgressId);
  if (progressId === 0 || progressId === -1) return 1;
  if (progressId === 1) return WOI_STATUS_BAR.length;
  return progressId;
};

const getWithdrawalStepObjection = (row) =>
  toProgressId(row?.applicationProgressId) === -1 ? 1 : 0;

const DetailField = ({ label, value, className = '' }) => (
  <div className={`first-row-item ${className}`.trim()}>
    <p className="prop-title">{label}</p>
    <p className="prop-detail">{value}</p>
  </div>
);

const AmountField = ({ label, value }) => (
  <div className="item">
    <Typography className="label">{label}</Typography>
    <Typography className="value">{value}</Typography>
  </div>
);

const formatRowDate = (value) => {
  if (!value) return '';
  const formatted = convertDate(value);
  if (!formatted || formatted.includes('undefined') || formatted.includes('NaN')) return String(value).slice(0, 10);
  return formatted;
};

const mapApplicationRow = (item) => ({
  applicationId: item.applicationId || item.APPLICATION_ID || item.eOpsTrackingId || item.trackingId || '',
  creationDate: formatRowDate(item.creationDate || item.CREATION_DATE || item.createdOn || item.submissionDate),
  referenceNo: item.referenceNo || item.REFERENCE_NO || item.refNo || item.REF_NO || '',
  applicantName: item.applicantName || item.APPLICANT_NAME || item.consumerName || item.CONSUMER_NAME || '',
  noOfInstallments: item.noOfInstallments ?? item.NO_OF_INSTALLMENTS ?? item.noOfMonthsForInstallment ?? '',
  billMonthAmount: numberFormatting(
    item.withdrawalAmount ?? item.WITHDRAWAL_AMOUNT ?? item.billMonthAmount ?? item.netPayableWhileInstallment ?? 0,
  ),
  applicationStatusId: statusFunc(item.applicationProgressId ?? item.APPLICATION_PROGRESS_ID, item.objectionStatus ?? item.OBJECTION_STATUS),
  applicationProgressId: item.applicationProgressId ?? item.APPLICATION_PROGRESS_ID,
  releventApplicationFlage: item.releventApplicationFlage,
});

const normalizeWithdrawalApplication = (item = {}) => ({
  ...item,
  applicationId: item.applicationId || item.APPLICATION_ID || item.eOpsTrackingId || item.trackingId || '',
  applicationProgressId: item.applicationProgressId ?? item.APPLICATION_PROGRESS_ID,
  referenceNo: item.referenceNo || item.REFERENCE_NO || item.refNo || item.REF_NO || '',
  customerId: item.customerId || item.CUSTOMER_ID || '',
  consumerName: item.consumerName || item.CONSUMER_NAME || item.applicantName || item.name || '',
  consumerCNIC: item.consumerCNIC || item.CONSUMER_CNIC || item.applicantCNIC || item.cnic || '',
  consumerPhone: item.consumerPhone || item.CONSUMER_PHONE || item.applicantPhone || item.phone || '',
  consumerEmail: item.consumerEmail || item.CONSUMER_EMAIL || item.applicantEmail || item.email || '',
  consumerAddress: item.consumerAddress || item.CONSUMER_ADDRESS || item.applicantAddress || item.address || '',
  telcoCode: item.telcoCode || item.TELCO_CODE,
  connectionType: item.connectionType || item.CONNECTION_TYPE,
  tariffDesc: item.tariffDesc || item.TARIFF_DESC || item.tariffCode || item.TARIFF_CODE,
  tariffCode: item.tariffCode || item.TARIFF_CODE,
  subDivisionCode: item.subDivisionCode || item.SUB_DIVISION_CODE,
  submittedThrough: item.submittedThrough || item.SUBMITTED_THROUGH,
  noOfInstallments: item.noOfInstallments ?? item.NO_OF_INSTALLMENTS ?? item.noOfMonthsForInstallment,
  installmentBillMonth: item.installmentBillMonth || item.INSTALLMENT_BILL_MONTH || item.billMonth,
  startMonth: item.startMonth || item.START_MONTH,
  endMonth: item.endMonth || item.END_MONTH,
  IPSAdjustmentNo: item.IPSAdjustmentNo || item.ipsAdjustmentNo,
  IPSAdjustmentDate: item.IPSAdjustmentDate || item.ipsAdjustmentDate,
  withdrawalAmount: item.withdrawalAmount ?? item.WITHDRAWAL_AMOUNT,
  netPayableWhileInstallment: item.netPayableWhileInstallment ?? item.NET_PAYABLE_WHILE_INSTALLMENT,
  withdrawalReason: item.withdrawalReason || item.WITHDRAWAL_REASON || item.reason,
  createdBy: item.createdBy || item.CREATED_BY || item.applicantName || item.consumerName,
  creationDate: item.creationDate || item.CREATION_DATE,
  lastModifiedBy: item.lastModifiedBy || item.LAST_MODIFIED_BY || item.approvedBy,
  lastModificationDate: item.lastModificationDate || item.LAST_MODIFICATION_DATE,
  remarks: item.remarks || item.REMARKS,
});

const mapApplicationsToGrid = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((item) => item?.applicationId || item?.APPLICATION_ID || item?.eOpsTrackingId || item?.trackingId)
    .map(mapApplicationRow);
};

const statusColumn = (style) => ({
  field: 'applicationStatusId',
  headerStyle: style.headerStyle,
  headerCellStyle: style.headerCellStyle,
  rowCellStyle: style.rowCellStyle,
  headerName: 'Status',
  align: 'center',
  sortable: false,
  filterable: true,
  type: 'singleSelect',
  valueOptions: ['In Process', 'Approved', 'Rejected'],
  renderCell: (params) => {
    const btnText = params.applicationStatusId;
    return <div className={getClassName(btnText)}>{btnText}</div>;
  },
});

const buildColumns = (style) => [
  {
    field: 'applicationId',
    headerName: 'Application No.',
    align: 'left',
    sortable: false,
    filterable: false,
    showFullApplicationId: true,
    preserveColumnWidth: true,
    headerStyle: {
      ...style.headerStyle,
      width: '22%',
      minWidth: '210px',
      maxWidth: '240px',
    },
    headerCellStyle: style.headerCellStyle,
    rowCellStyle: {
      ...style.rowCellStyle,
      minWidth: '210px',
      maxWidth: '240px',
    },
  },
  { field: 'creationDate', type: 'dateTime', headerName: 'Submission Date', align: 'center', sortable: false, filterable: false, headerStyle: style.headerStyle, headerCellStyle: style.headerCellStyle, rowCellStyle: style.rowCellStyle },
  { field: 'referenceNo', headerName: 'Reference No.', align: 'center', sortable: false, filterable: false, headerStyle: style.headerStyle, headerCellStyle: style.headerCellStyle, rowCellStyle: style.rowCellStyle },
  { field: 'applicantName', headerName: 'Applicant Name', align: 'left', sortable: false, filterable: false, headerStyle: style.headerStyle, headerCellStyle: style.headerCellStyle, rowCellStyle: style.rowCellStyle },
  { field: 'noOfInstallments', type: 'number', headerName: 'No. of Installments', align: 'center', sortable: false, filterable: false, headerStyle: style.headerStyle, headerCellStyle: style.headerCellStyle, rowCellStyle: style.rowCellStyle },
  { field: 'billMonthAmount', headerName: 'Bill Amount', align: 'center', sortable: false, filterable: false, headerStyle: style.headerStyle, headerCellStyle: style.headerCellStyle, rowCellStyle: style.rowCellStyle },
  statusColumn(style),
];

const WithdrawalOfInstallmentTable = forwardRef((props, ref) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const dispatch = useDispatch();
  const { token, userId, code: officeCode, selectedAdminCompanyCode } = useSelector((state) => state.auth);
  const isWorkflowMobile = useMediaQuery(WORKFLOW_MOBILE_MEDIA);
  const subDivisions = JSON.parse(window.localStorage.getItem('subDivisions'));
  const [open, setOpen] = useState(false);
  const [dialogRowData, setDialogRowData] = useState({});
  const [tableDataProcessingSummary, setTableDataProcessingSummary] = useState([]);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [error, setError] = useState('');
  const [resultPopup, setResultPopup] = useState(null);
  const [selectedOfficeCode, setSelectedOfficeCode] = useState(
    getInitialAdminOfficeCode(officeCode, selectedAdminCompanyCode),
  );
  const apiOfficeCode = String(officeCode) === '10'
    ? selectedOfficeCode
    : (selectedOfficeCode || officeCode);
  const isTableVisible = Boolean(props.showTable && currentPath === '/withdrawal-of-installment-table');
  const canFetch = Boolean(isTableVisible && token && apiOfficeCode);

  const {
    currentPage: currentPageNormal,
    data: dataNormal,
    pageSize: pageSizeNormal,
    totalPages: totalPagesNormal,
    totalRecords: totalRecordsNormal,
    dateFrom: dateFromNormal,
    dateTo: dateToNormal,
    applicationProgressId: applicationProgressIdNormal,
    loadFrom: loadFromNormal,
    loadTo: loadToNormal,
    isLoading: isLoadingNormal,
  } = useSelector((state) => state.withdrawalOfInstallmentNormalPaginated);

  const {
    currentPage: currentPageOverdue,
    data: dataOverdue,
    pageSize: pageSizeOverdue,
    totalPages: totalPagesOverdue,
    totalRecords: totalRecordsOverdue,
    dateFrom: dateFromOverdue,
    dateTo: dateToOverdue,
    applicationProgressId: applicationProgressIdOverdue,
    loadFrom: loadFromOverdue,
    loadTo: loadToOverdue,
    isLoading: isLoadingOverdue,
  } = useSelector((state) => state.withdrawalOfInstallmentOverduePaginated);

  useEffect(() => {
    if (String(officeCode) === '10' && selectedAdminCompanyCode) {
      setSelectedOfficeCode(String(selectedAdminCompanyCode));
    }
  }, [officeCode, selectedAdminCompanyCode]);

  useEffect(() => {
    if (canFetch) {
      dispatch(fetchPaginatedWithdrawalOfInstallmentNormal({
        page: currentPageNormal,
        pageSize: pageSizeNormal,
        dateFrom: dateFromNormal,
        dateTo: dateToNormal,
        applicationProgressId: applicationProgressIdNormal,
        loadFrom: loadFromNormal,
        loadTo: loadToNormal,
        officeCode: apiOfficeCode,
      }));
    }
  }, [
    canFetch,
    currentPageNormal,
    pageSizeNormal,
    dateFromNormal,
    dateToNormal,
    applicationProgressIdNormal,
    loadFromNormal,
    loadToNormal,
    apiOfficeCode,
    dispatch,
  ]);

  useEffect(() => {
    if (canFetch) {
      dispatch(fetchPaginatedWithdrawalOfInstallmentOverdue({
        page: currentPageOverdue,
        pageSize: pageSizeOverdue,
        dateFrom: dateFromOverdue,
        dateTo: dateToOverdue,
        applicationProgressId: applicationProgressIdOverdue,
        loadFrom: loadFromOverdue,
        loadTo: loadToOverdue,
        officeCode: apiOfficeCode,
      }));
    }
  }, [
    canFetch,
    currentPageOverdue,
    pageSizeOverdue,
    dateFromOverdue,
    dateToOverdue,
    applicationProgressIdOverdue,
    loadFromOverdue,
    loadToOverdue,
    apiOfficeCode,
    dispatch,
  ]);

  const handleNextPage = () => dispatch(nextPageNormal());
  const handlePreviousPage = () => dispatch(previousPageNormal());
  const handleFirstPage = () => dispatch(firstPageNormal());
  const handleLastPage = (page) => dispatch(lastPageNormal(typeof page === 'number' ? page : totalPagesNormal));
  const handleDateFromFilter = (value) => {
    if (value) {
      dispatch(firstPageNormal());
      dispatch(filterDateFromNormal(value));
    }
  };
  const handleDateToFilter = (value) => {
    if (value) {
      dispatch(firstPageNormal());
      dispatch(filterDateToNormal(value));
    }
  };
  const handleResetDateFilter = () => {
    dispatch(firstPageNormal());
    dispatch(resetDateFilterNormal());
  };
  const handleProgressIdFilter = (value) => {
    if (value || value === 0) {
      dispatch(firstPageNormal());
      dispatch(filterProgressIdNormal(value));
    }
  };
  const handleResetProgressIdFilter = () => {
    dispatch(firstPageNormal());
    dispatch(resetProgressIdFilterNormal());
  };

  const handleNextPageOverdue = () => dispatch(nextPageOverdue());
  const handlePreviousPageOverdue = () => dispatch(previousPageOverdue());
  const handleFirstPageOverdue = () => dispatch(firstPageOverdue());
  const handleLastPageOverdue = (page) => dispatch(lastPageOverdue(typeof page === 'number' ? page : totalPagesOverdue));
  const handleDateFromFilterOverdue = (value) => {
    if (value) {
      dispatch(firstPageOverdue());
      dispatch(filterDateFromOverdue(value));
    }
  };
  const handleDateToFilterOverdue = (value) => {
    if (value) {
      dispatch(firstPageOverdue());
      dispatch(filterDateToOverdue(value));
    }
  };
  const handleResetDateFilterOverdue = () => {
    dispatch(firstPageOverdue());
    dispatch(resetDateFilterOverdue());
  };
  const handleProgressIdFilterOverdue = (value) => {
    if (value || value === 0) {
      dispatch(firstPageOverdue());
      dispatch(filterProgressIdOverdue(value));
    }
  };
  const handleResetProgressIdFilterOverdue = () => {
    dispatch(firstPageOverdue());
    dispatch(resetProgressIdFilterOverdue());
  };

  const handleClose = () => {
    setOpen(false);
    setDialogRowData({});
    setTableDataProcessingSummary([]);
    setError('');
    setOpenCancelModal(false);
  };

  const refreshApplications = () => {
    if (!canFetch) return;
    dispatch(fetchPaginatedWithdrawalOfInstallmentNormal({
      page: currentPageNormal,
      pageSize: pageSizeNormal,
      dateFrom: dateFromNormal,
      dateTo: dateToNormal,
      applicationProgressId: applicationProgressIdNormal,
      loadFrom: loadFromNormal,
      loadTo: loadToNormal,
      officeCode: apiOfficeCode,
    }));
    dispatch(fetchPaginatedWithdrawalOfInstallmentOverdue({
      page: currentPageOverdue,
      pageSize: pageSizeOverdue,
      dateFrom: dateFromOverdue,
      dateTo: dateToOverdue,
      applicationProgressId: applicationProgressIdOverdue,
      loadFrom: loadFromOverdue,
      loadTo: loadToOverdue,
      officeCode: apiOfficeCode,
    }));
  };

  const processApplication = async (applicationProgressId, remarks) => {
    if (!dialogRowData.applicationId) return;
    setViewBackdrop(true);
    setError('');
    try {
      const result = await processInstallmentWithdrawal(token, {
        applicationId: dialogRowData.applicationId,
        remarks,
        applicationProgressId,
      });
      if (String(result?.status) === '200') {
        setOpenCancelModal(false);
        handleClose();
        refreshApplications();
        setResultPopup(applicationProgressId === 1 ? 'approve' : 'reject');
      } else {
        setError(result?.message || result?.innerMessage || 'Unable to process withdrawal of installment.');
      }
    } catch (e) {
      setError('An error occurred while processing withdrawal of installment.');
    } finally {
      setViewBackdrop(false);
    }
  };

  const remarksFormik = useFormik({
    initialValues: { iv_remarks: '' },
    validationSchema: Yup.object({
      iv_remarks: Yup.string()
        .min(10, 'Remarks should be at least 10 characters.')
        .max(100, 'Remarks should be less than 100 characters.')
        .required('Enter remarks.'),
    }),
    onSubmit: async (values) => {
      await processApplication(-1, values.iv_remarks);
    },
  });

  const handleCloseRemarks = () => {
    remarksFormik.resetForm();
    setOpenCancelModal(false);
    setError('');
  };

  const filterWithdrawalApplication = async (applicationId, searchRow) => {
    const localItem = [...(dataNormal || []), ...(dataOverdue || [])]
      .find((row) => String(row.applicationId || row.APPLICATION_ID || row.eOpsTrackingId) === String(applicationId));
    if (localItem) return localItem;

    const fetched = await SearchIndividualApplication(token, applicationId, '19');
    if (fetched) return fetched;

    if (searchRow && typeof searchRow === 'object') {
      const searchId = searchRow.applicationId || searchRow.eOpsTrackingId;
      if (searchId && String(searchId) === String(applicationId)) {
        return searchRow;
      }
    }
    return null;
  };

  const openWithdrawalDialog = (rawItem) => {
    const item = normalizeWithdrawalApplication(rawItem);
    if (!item.applicationId) return;

    const progress = toProgressId(item.applicationProgressId);
    const log = [];
    if (progress === 0) {
      log.push({
        stepName: 'Application Submitted',
        processedBy: item.createdBy || item.consumerName || '-',
        date: item.creationDate ? convertDatewithTime(item.creationDate) : '-',
        remarks: 'Application Submitted.',
      });
    } else {
      log.push({
        stepName: progress === -1 ? 'Request Declined' : 'Action Taken',
        processedBy: item.lastModifiedBy || item.approvedBy || '-',
        date: item.lastModificationDate ? convertDatewithTime(item.lastModificationDate) : '-',
        remarks: item.remarks || item.withdrawalReason || '-',
      });
      log.push({
        stepName: 'Application Submitted',
        processedBy: item.createdBy || item.consumerName || '-',
        date: item.creationDate ? convertDatewithTime(item.creationDate) : '-',
        remarks: 'Application Submitted.',
      });
    }
    setTableDataProcessingSummary(log);
    setDialogRowData(item);
    setOpen(true);
  };

  const handleRowClick = async (applicationId, extra) => {
    if (!applicationId) return;
    const searchRow = extra && typeof extra === 'object' ? extra : null;
    setViewBackdrop(true);
    try {
      const item = await filterWithdrawalApplication(applicationId, searchRow);
      if (item) {
        openWithdrawalDialog(item);
      }
    } finally {
      setViewBackdrop(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleRowClick,
  }));

  const openDetailsPopUp = () => { };

  const statusBar = WOI_STATUS_BAR;

  const activityLogColumns = [
    { header: 'Step', accessorKey: 'stepName' },
    { header: 'Processed By', accessorKey: 'processedBy' },
    { header: 'Date', accessorKey: 'date' },
    { header: 'Remarks', accessorKey: 'remarks' },
  ];

  const progress = toProgressId(dialogRowData.applicationProgressId);

  const normalData = mapApplicationsToGrid(dataNormal);
  const overdueData = mapApplicationsToGrid(dataOverdue);

  return (
    <>
      <ServiceDashboardTables
        enabled={isTableVisible}
        comfortableRows
        isLoadingNormal={isLoadingNormal}
        isLoadingOverdue={isLoadingOverdue}
        normalTitle={`Withdrawal of Installment ( ${numberFormatting(totalRecordsNormal) ? numberFormatting(totalRecordsNormal) : '-'} )`}
        overdueCount={numberFormatting(totalRecordsOverdue) ? numberFormatting(totalRecordsOverdue) : '-'}
        normalColumns={buildColumns(tableStyle)}
        overdueColumns={buildColumns(tableStyleOverDue)}
        normalData={normalData}
        overdueData={overdueData}
        onRowClick={handleRowClick}
        mobileFields={['applicationId', 'applicantName', 'applicationStatusId']}
        showCompanyFilter={officeCode == 10}
        companies={DISCO_COMPANIES}
        selectedOfficeCode={selectedOfficeCode}
        onOfficeCodeChange={setSelectedOfficeCode}
        normalTableProps={{
          firstPage: handleFirstPage,
          lastPage: handleLastPage,
          nextPage: handleNextPage,
          previousPage: handlePreviousPage,
          handleFilterDateFrom: handleDateFromFilter,
          handleFilterDateTo: handleDateToFilter,
          resetDateFilter: handleResetDateFilter,
          handleFilterProgressId: handleProgressIdFilter,
          resetProgressIdFilter: handleResetProgressIdFilter,
          totalPages: totalPagesNormal,
          totalRecords: totalRecordsNormal,
          currentPage: currentPageNormal,
        }}
        overdueTableProps={{
          firstPage: handleFirstPageOverdue,
          lastPage: handleLastPageOverdue,
          nextPage: handleNextPageOverdue,
          previousPage: handlePreviousPageOverdue,
          handleFilterDateFrom: handleDateFromFilterOverdue,
          handleFilterDateTo: handleDateToFilterOverdue,
          resetDateFilter: handleResetDateFilterOverdue,
          handleFilterProgressId: handleProgressIdFilterOverdue,
          resetProgressIdFilter: handleResetProgressIdFilterOverdue,
          totalPages: totalPagesOverdue,
          totalRecords: totalRecordsOverdue,
          currentPage: currentPageOverdue,
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        {...workflowDialogShellProps(isWorkflowMobile)}
        className={workflowDialogClassName('main-dialog inst-workflow-dialog', isWorkflowMobile)}
        PaperProps={{ className: 'inst-workflow-dialog-paper' }}
      >
        <DialogTitle className="dialog-title">
          <div className="inst-dialog-header">
            <Typography className="applicationTitleText">
              Withdrawal of Installment -
              <span style={{ color: 'white' }}>
                {progress === 0 && ' Application Submitted'}
                {progress === 1 && ' Completed'}
                {progress === -1 && ' Request Declined'}
              </span>
            </Typography>
            <button type="button" className="closeIcon" onClick={handleClose} aria-label="Close dialog">
              <CloseIcon id="clearPopup" sx={{ color: 'white' }} />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="dialog-content" component="div">
            <Grid container className="headercontainer">
              <Grid item lg={12} className="status-bar">
                <StepProgressBar
                  array={statusBar}
                  applicationProgressId={getWithdrawalStepIndex(dialogRowData)}
                  objection={getWithdrawalStepObjection(dialogRowData)}
                  openDetailsPopUp={openDetailsPopUp}
                  pageName="Withdrawal of Installment"
                />
              </Grid>
              <Grid item xs={12} className="application-details">
                <div className="inst-application-details-body">
                  <p className="inst-section-label">Application Details</p>
                  <div className="inst-details-grid">
                    <DetailField label="Application Id." value={displayValue(dialogRowData.applicationId)} />
                    <DetailField label="Reference No." value={displayValue(dialogRowData.referenceNo)} />
                    <DetailField label="Customer ID" value={displayValue(dialogRowData.customerId)} />
                    <DetailField label="Connection Type" value={displayValue(dialogRowData.connectionType)} />
                    <DetailField label="Tariff" value={displayValue(dialogRowData.tariffDesc || dialogRowData.tariffCode)} />
                    <DetailField
                      label="Subdivision"
                      value={dialogRowData.subDivisionCode
                        ? subdivisionName(dialogRowData.subDivisionCode, subDivisions)
                        : '-'}
                    />
                    <DetailField label="Applicant Name" value={displayValue(dialogRowData.consumerName)} />
                    <DetailField label="Applicant CNIC" value={displayValue(dialogRowData.consumerCNIC)} />
                    <DetailField label="Applicant Phone" value={displayValue(dialogRowData.consumerPhone)} />
                    <DetailField label="Telco" value={telcoLabel(dialogRowData.telcoCode)} />
                    <DetailField label="Applicant Email" value={displayValue(dialogRowData.consumerEmail)} />
                    <DetailField
                      label="Address"
                      value={displayValue(dialogRowData.consumerAddress)}
                      className="inst-details-grid__address"
                    />
                  </div>
                </div>
              </Grid>
              <Grid item className="processing-summary">
                <p className="title">Activity Log</p>
                <Grid container>
                  <Grid item xs={12}>
                    <ProcessingSummaryTableBilling
                      data={tableDataProcessingSummary}
                      columns={activityLogColumns}
                      openDetailsPopUp={openDetailsPopUp}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item className="action-form woi-action-form">
                <div className="fields">
                  {progress === 0 && (
                    <h3 className="step-title">
                      Application Received from {displayValue(dialogRowData.submittedThrough)}
                    </h3>
                  )}
                  {progress === 1 && <h3 className="step-title">Withdrawal Completed</h3>}
                  {progress === -1 && (
                    <>
                      <h3 className="step-title">Request Declined</h3>
                      <div className="description-box description-box--error">
                        <p className="group-description">
                          <InfoIcon className="icon" /> Application rejected
                        </p>
                      </div>
                    </>
                  )}
                  <div className="sco-container woi-details-grid">
                    <AmountField label="No. of Installments" value={displayValue(dialogRowData.noOfInstallments)} />
                    <AmountField label="Installment Bill Month" value={displayMonth(dialogRowData.installmentBillMonth)} />
                    <AmountField label="Start Month" value={displayMonth(dialogRowData.startMonth)} />
                    <AmountField label="End Month" value={displayMonth(dialogRowData.endMonth)} />
                    {/* <AmountField label="Adjustment No." value={displayValue(dialogRowData.IPSAdjustmentNo)} />
                                        <AmountField label="Adjustment Date" value={displayDate(dialogRowData.IPSAdjustmentDate)} /> */}
                    <AmountField label="Withdrawal Amount" value={displayAmount(dialogRowData.withdrawalAmount)} />
                    <AmountField label="Net Payable While Installment" value={displayAmount(dialogRowData.netPayableWhileInstallment)} />
                    <AmountField label="Net for Bill" value={displayAmount(dialogRowData.netForBill)} />
                    {/* <AmountField label="Paid Installments Amount" value={displayAmount(dialogRowData.paidInstallmentsAmount)} /> */}
                  </div>
                  <div className="woi-reason-box">
                    <Typography className="woi-reason-box__label">Reason for Withdrawal</Typography>
                    <Typography className="woi-reason-box__value">
                      {displayValue(dialogRowData.withdrawalReason)}
                    </Typography>
                  </div>
                  {error ? (
                    <Error
                      error={error}
                      onClose={() => setError('')}
                      backgroundColor="#FFC0CB"
                      textColor="#bb0303"
                      iconColor="#bb0303"
                    />
                  ) : null}
                </div>
                {progress === 0 && (
                  <div className="actions">
                    <button
                      type="button"
                      className="view-button"
                      onClick={() => processApplication(1, 'Approved.')}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="reject-button"
                      onClick={() => setOpenCancelModal(true)}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openCancelModal}
        className="inst-remarks-dialog"
        onClose={handleCloseRemarks}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'inst-remarks-dialog__paper' }}
      >
        <DialogTitle className="inst-remarks-dialog__title">
          <div className="inst-remarks-dialog__header">
            <Typography className="inst-remarks-dialog__heading">Remarks</Typography>
            <button
              type="button"
              className="inst-remarks-dialog__close"
              onClick={handleCloseRemarks}
              aria-label="Close remarks dialog"
            >
              <CloseIcon id="clearPopup" />
            </button>
          </div>
        </DialogTitle>
        <DialogContent className="inst-remarks-dialog__content">
          <TextField
            autoFocus
            id="iv_remarks"
            placeholder="Enter remarks for rejection (minimum 10 characters)"
            type="text"
            fullWidth
            variant="outlined"
            onChange={remarksFormik.handleChange}
            onBlur={remarksFormik.handleBlur}
            name="iv_remarks"
            value={remarksFormik.values.iv_remarks}
            multiline
            minRows={5}
            maxRows={8}
            className="inst-remarks-dialog__textarea"
            error={Boolean(remarksFormik.touched.iv_remarks && remarksFormik.errors.iv_remarks)}
          />
          {remarksFormik.touched.iv_remarks && remarksFormik.errors.iv_remarks ? (
            <p className="inst-remarks-dialog__error">{remarksFormik.errors.iv_remarks}</p>
          ) : null}
          {error ? (
            <Error
              error={error}
              onClose={() => setError('')}
              backgroundColor="#FFC0CB"
              textColor="#bb0303"
              iconColor="#bb0303"
            />
          ) : null}
        </DialogContent>
        <DialogActions className="inst-remarks-dialog__actions">
          <Button
            type="button"
            onClick={remarksFormik.handleSubmit}
            className="inst-remarks-dialog__btn inst-remarks-dialog__btn--confirm"
          >
            Confirm
          </Button>
          <Button
            type="button"
            onClick={handleCloseRemarks}
            className="inst-remarks-dialog__btn inst-remarks-dialog__btn--cancel"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(resultPopup)}
        onClose={() => setResultPopup(null)}
        className={`dde-result-dialog${resultPopup === 'reject' ? ' dde-result-dialog--reject' : ''}`}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: 'dde-result-dialog__paper' }}
      >
        <DialogTitle className="dde-result-dialog__title">
          {resultPopup === 'approve' ? (
            <CheckCircleIcon className="dde-result-dialog__icon dde-result-dialog__icon--approve" />
          ) : (
            <HighlightOffIcon className="dde-result-dialog__icon dde-result-dialog__icon--reject" />
          )}
          <Typography>
            {resultPopup === 'approve' ? 'Request Approved' : 'Request Rejected'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {resultPopup === 'approve'
              ? 'Withdrawal of installment request is Approved successfully.'
              : 'Withdrawal of installment request is Rejected successfully.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setResultPopup(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={viewBackdrop}>
        <AppLoader />
      </Backdrop>
    </>
  );
});

WithdrawalOfInstallmentTable.displayName = 'WithdrawalOfInstallmentTable';

export default WithdrawalOfInstallmentTable;
